import { useEffect, useMemo, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Collapse from 'react-bootstrap/Collapse'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'

import AuthService from '../../utilities/services/auth.service'
import {
  deleteDeferredRevenueEmailSubscription,
  getDeferredRevenueEmailSubscription,
  saveDeferredRevenueEmailSubscription,
  sendDeferredRevenueTestEmail,
} from '../../utilities/api'

const WEEK_DAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
]

const MONTH_DAY_OPTIONS = Array.from({ length: 28 }, (_, index) => ({
  value: index + 1,
  label: String(index + 1),
}))

const DEFAULT_SCHEDULE = {
  enabled: false,
  frequency: 'monthly',
  dayOfWeek: 1,
  dayOfMonth: 1,
  hour: 8,
  minute: 0,
  timezone: 'America/New_York',
  email: '',
}

const padTimeUnit = (value) => String(value).padStart(2, '0')

const toTimeInputValue = (hour, minute) =>
  `${padTimeUnit(hour)}:${padTimeUnit(minute)}`

const parseTimeInputValue = (value) => {
  const [hourPart, minutePart] = String(value || '').split(':')
  const hour = Number.parseInt(hourPart, 10)
  const minute = Number.parseInt(minutePart, 10)
  return {
    hour: Number.isFinite(hour) ? hour : DEFAULT_SCHEDULE.hour,
    minute: Number.isFinite(minute) ? minute : DEFAULT_SCHEDULE.minute,
  }
}

const periodLabelForFrequency = (frequency) => {
  if (frequency === 'daily') return 'day'
  if (frequency === 'weekly') return 'week'
  if (frequency === 'quarterly') return 'quarter'
  return 'month'
}

export default function DeferredRevenueEmailSchedule({
  organizationUUID,
  selectedSeason,
  includeAdvanceTickets,
  asOfDate,
}) {
  const defaultEmail = AuthService.getUser()?.user?.email || ''
  const [schedule, setSchedule] = useState({
    ...DEFAULT_SCHEDULE,
    email: defaultEmail,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [isSendingTest, setIsSendingTest] = useState(false)
  const [error, setError] = useState('')
  const [savedMessage, setSavedMessage] = useState('')

  useEffect(() => {
    let cancelled = false

    const loadSubscription = async () => {
      if (!organizationUUID) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setError('')

      try {
        const response =
          await getDeferredRevenueEmailSubscription(organizationUUID)
        if (cancelled) return

        const loadedSchedule = response?.data?.schedule || {}
        setSchedule({
          ...DEFAULT_SCHEDULE,
          ...loadedSchedule,
          email: loadedSchedule.email || defaultEmail,
        })
      } catch (loadError) {
        if (cancelled) return
        console.error(
          'Error loading Season Deferred Revenue email schedule:',
          loadError
        )
        setError('Unable to load email schedule settings.')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadSubscription()

    return () => {
      cancelled = true
    }
  }, [organizationUUID, defaultEmail])

  const deliveryTimeValue = useMemo(
    () => toTimeInputValue(schedule.hour, schedule.minute),
    [schedule.hour, schedule.minute]
  )

  const buildPayload = () => ({
    organizationUUID,
    seasonId: selectedSeason || null,
    includeAdvanceTickets: Boolean(includeAdvanceTickets),
    schedule,
  })

  const handleSave = async () => {
    setIsSaving(true)
    setError('')
    setSavedMessage('')

    try {
      await saveDeferredRevenueEmailSubscription(buildPayload())
      setSavedMessage(
        'Email schedule saved. Scheduled sends use the season and advance-ticket setting currently selected on this page.'
      )
    } catch (saveError) {
      console.error(
        'Error saving Season Deferred Revenue email schedule:',
        saveError
      )
      setError(
        saveError?.response?.data?.error?.message ||
          'Unable to save email schedule. Please try again.'
      )
    } finally {
      setIsSaving(false)
    }
  }

  const handleSendTest = async () => {
    setIsSendingTest(true)
    setError('')
    setSavedMessage('')

    try {
      const response = await sendDeferredRevenueTestEmail({
        ...buildPayload(),
        asOfDate: asOfDate ? new Date(asOfDate).toISOString() : undefined,
      })
      const email = response?.data?.email || schedule.email
      setSavedMessage(`Test email sent to ${email}.`)
    } catch (sendError) {
      console.error(
        'Error sending Season Deferred Revenue test email:',
        sendError
      )
      setError(
        sendError?.response?.data?.error?.message ||
          'Unable to send test email. Please try again.'
      )
    } finally {
      setIsSendingTest(false)
    }
  }

  const handleEnabledChange = async (event) => {
    const enabled = event.target.checked

    if (enabled) {
      setError('')
      setSavedMessage('')
      setSchedule((prev) => ({ ...prev, enabled: true }))
      return
    }

    setError('')
    setSavedMessage('')
    setSchedule((prev) => ({ ...prev, enabled: false }))
    setIsRemoving(true)

    try {
      await deleteDeferredRevenueEmailSubscription(organizationUUID)
      setSchedule({
        ...DEFAULT_SCHEDULE,
        email: defaultEmail,
      })
      setSavedMessage('Email schedule removed.')
    } catch (removeError) {
      console.error(
        'Error removing Season Deferred Revenue email schedule:',
        removeError
      )
      setSchedule((prev) => ({ ...prev, enabled: true }))
      setError(
        removeError?.response?.data?.error?.message ||
          'Unable to remove email schedule. Please try again.'
      )
    } finally {
      setIsRemoving(false)
    }
  }

  const isBusy = isLoading || isSaving || isSendingTest || isRemoving

  return (
    <Card body className="mb-4 card-with-border">
      <div className="d-flex justify-content-between align-items-center gap-3">
        <div>
          <h2 className="card-title-sm mb-1">Email report</h2>
          <p className="small text-muted mb-0">
            Receive this report as an Excel attachment on a daily, weekly,
            monthly, or quarterly schedule. Scheduled emails cover the previous
            completed period (for monthly, last month-end).
          </p>
        </div>
        <Form.Check
          type="switch"
          id="deferred-revenue-email-schedule-enabled"
          label="Enabled"
          checked={Boolean(schedule.enabled)}
          disabled={isBusy || !selectedSeason}
          aria-controls="deferred-revenue-email-schedule-body"
          aria-expanded={Boolean(schedule.enabled)}
          onChange={handleEnabledChange}
        />
      </div>

      {error ? <p className="text-danger small mb-0 mt-2">{error}</p> : null}
      {savedMessage ? (
        <p className="text-success small mb-0 mt-2">{savedMessage}</p>
      ) : null}

      <Collapse in={Boolean(schedule.enabled)}>
        <div id="deferred-revenue-email-schedule-body" className="pt-3">
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small fw-semibold mb-1">
                Frequency
              </Form.Label>
              <Form.Select
                value={schedule.frequency}
                disabled={isBusy}
                onChange={(event) =>
                  setSchedule((prev) => ({
                    ...prev,
                    frequency: event.target.value,
                  }))
                }
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </Form.Select>
            </Col>

            {schedule.frequency === 'weekly' ? (
              <Col md={3}>
                <Form.Label className="small fw-semibold mb-1">Day</Form.Label>
                <Form.Select
                  value={Number(schedule.dayOfWeek ?? 1)}
                  disabled={isBusy}
                  onChange={(event) =>
                    setSchedule((prev) => ({
                      ...prev,
                      dayOfWeek: Number(event.target.value),
                    }))
                  }
                >
                  {WEEK_DAY_OPTIONS.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : null}

            {schedule.frequency === 'monthly' ||
            schedule.frequency === 'quarterly' ? (
              <Col md={3}>
                <Form.Label className="small fw-semibold mb-1">
                  {schedule.frequency === 'quarterly'
                    ? 'Day of quarter start month'
                    : 'Day of month'}
                </Form.Label>
                <Form.Select
                  value={Number(schedule.dayOfMonth ?? 1)}
                  disabled={isBusy}
                  onChange={(event) =>
                    setSchedule((prev) => ({
                      ...prev,
                      dayOfMonth: Number(event.target.value),
                    }))
                  }
                >
                  {MONTH_DAY_OPTIONS.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ) : null}

            <Col md={3}>
              <Form.Label className="small fw-semibold mb-1">
                Delivery time (EST)
              </Form.Label>
              <Form.Control
                type="time"
                value={deliveryTimeValue}
                disabled={isBusy}
                onChange={(event) => {
                  const { hour, minute } = parseTimeInputValue(
                    event.target.value
                  )
                  setSchedule((prev) => ({ ...prev, hour, minute }))
                }}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-semibold mb-1">Email</Form.Label>
              <Form.Control
                type="email"
                value={schedule.email}
                placeholder="you@example.com"
                disabled={isBusy}
                onChange={(event) =>
                  setSchedule((prev) => ({
                    ...prev,
                    email: event.target.value,
                  }))
                }
              />
            </Col>

            <Col md="auto" className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                className="form-control-height"
                disabled={isBusy || !schedule.email || !selectedSeason}
                onClick={handleSendTest}
              >
                {isSendingTest ? 'Sending…' : 'Send Test Email Now'}
              </Button>
              <Button
                variant="primary"
                className="form-control-height"
                disabled={isBusy || !selectedSeason}
                onClick={handleSave}
              >
                {isSaving ? 'Saving…' : 'Save Schedule'}
              </Button>
            </Col>
          </Row>

          <p className="text-muted small mb-0 mt-3">
            Save uses the currently selected season and advance-ticket toggle.
            Scheduled emails cover the previous{' '}
            {periodLabelForFrequency(schedule.frequency)}. Test emails use the
            as-of date shown on this page.
          </p>
        </div>
      </Collapse>
    </Card>
  )
}
